import React, { useState, useRef, useEffect, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, MinimizedModalStrip, SyutSelect } from 'components/ui';
import type { ActionButton, ViewModeOption } from 'components/modals/common/ModuleHeader';
import notificationService from '../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { roleService } from 'api/services';
import { statusService } from 'api/services';
import type { RoleDto, CreateRoleRequest, UpdateRoleRequest, RoleQueryParams, StatusDto } from 'api/types';

// ============================================================================
// ROLE FORM MODAL
// ============================================================================

interface RoleFormModalProps {
  open: boolean;
  mode: 'create' | 'edit' | 'copy';
  record: RoleDto | null;
  onClose: () => void;
  onSave: () => void;
  tabId?: string;
  moduleName?: string;
  instanceId?: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({
  open,
  mode,
  record,
  onClose,
  onSave,
  tabId,
  moduleName,
  instanceId,
  minimizeRef,
  onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState({
    roleCode: '',
    roleName: '',
    description: '',
    statusCode: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const savedSnapshot = useRef<string | null>(null);

  // Refs for minimize/restore — captures current state in closure
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const isRestoreRef = useRef(false);

  // Populate form when opened
  useEffect(() => {
    if (!open) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    if (mode === 'edit' && record) {
      const fd = {
        roleCode: record.roleCode,
        roleName: record.roleName,
        description: record.description || '',
        statusCode: record.statusCode || 'ACTIVE',
      };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    } else if (mode === 'copy' && record) {
      const fd = {
        roleCode: '',
        roleName: record.roleName + ' (Copy)',
        description: record.description || '',
        statusCode: record.statusCode || 'ACTIVE',
      };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    } else {
      const fd = { roleCode: '', roleName: '', description: '', statusCode: 'ACTIVE' };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    }
    setFieldErrors({});
  }, [open, mode, record]);

  // Async status loader (matches SampleModuleModal pattern)
  const loadStatusOptions = useCallback(async (inputValue: string) => {
    const statuses: StatusDto[] = await statusService.getByType('GENERAL');
    const filtered = inputValue
      ? statuses.filter(s => s.statusName.toLowerCase().includes(inputValue.toLowerCase()))
      : statuses;
    return filtered.map(s => ({ value: s.statusCode, label: s.statusName }));
  }, []);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentFormData = { ...formDataRef.current };
    const currentSnapshot = savedSnapshot.current || '';
    const currentIsDirty = currentSnapshot ? JSON.stringify(currentFormData) !== currentSnapshot : false;
    const idx = instanceId || '1';
    let title: string;
    if (mode === 'create' || mode === 'copy') {
      const prefix = mode === 'copy' ? 'Copy' : 'Add';
      title = currentFormData.roleCode
        ? `${prefix} - ${currentFormData.roleCode} (${idx})`
        : currentFormData.roleName
        ? `${prefix} - ${currentFormData.roleName} (${idx})`
        : `${prefix} New (${idx})`;
    } else {
      title = `Edit - ${record?.roleCode || currentFormData.roleCode}`;
    }
    minimize({
      id: tabId ? `${tabId}-role-modal-${mode === 'edit' ? record?.id : mode + '-' + instanceId}` : 'role-modal',
      tabId: tabId!,
      moduleName: moduleName || 'Role Master',
      title,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(currentFormData);
        savedSnapshot.current = currentSnapshot;
        onRestoreModal?.(mode, instanceId, mode !== 'create' ? record?.roleCode : undefined);
      },
    });
    onClose();
  }, [tabId, instanceId, mode, record, minimize, moduleName, onRestoreModal, onClose]);

  useEffect(() => {
    if (minimizeRef) minimizeRef.current = handleMinimize;
  }, [minimizeRef, handleMinimize]);

  const handleClose = useCallback(() => {
    const isDirty = savedSnapshot.current ? JSON.stringify(formData) !== savedSnapshot.current : false;
    if (isDirty) {
      setCloseConfirmOpen(true);
    } else {
      onClose();
    }
  }, [formData, onClose]);

  // Step 1: Validate required fields, then show confirm dialog (instant — no API calls)
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.roleCode.trim()) errors.roleCode = 'Role Code is required';
    if (!formData.roleName.trim()) errors.roleName = 'Role Name is required';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // Step 2: After user confirms — check code uniqueness + save
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      // Code uniqueness check (server-side validation as fallback)
      if (mode !== 'edit') {
        try {
          const available = await roleService.isCodeAvailable(formData.roleCode.trim());
          if (!available) {
            setSaving(false);
            setFieldErrors({ roleCode: 'This code already exists. Please use a different code.' });
            return;
          }
        } catch {
          // API unavailable — let server enforce uniqueness on create
        }
      }

      if (mode === 'edit' && record) {
        const req: UpdateRoleRequest = {
          roleCode: formData.roleCode.toUpperCase(),
          roleName: formData.roleName,
          description: formData.description || undefined,
          isActive: true,
          rowVersion: record.rowVersion,
        };
        await roleService.update(record.id, req);
        notificationService.toast.success(`Role "${formData.roleName}" has been updated successfully.`);
      } else {
        const req: CreateRoleRequest = {
          roleCode: formData.roleCode.toUpperCase(),
          roleName: formData.roleName,
          description: formData.description || undefined,
          isActive: true,
        };
        await roleService.create(req);
        notificationService.toast.success(`Role "${formData.roleName}" has been created successfully.`);
      }
      onSave();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'An error occurred while saving. Please try again.';
      notificationService.toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [formData, mode, record, onSave]);

  if (!open) return null;

  const title = mode === 'create' ? 'Add Role' : mode === 'copy' ? 'Copy Role' : `Edit Role — ${record?.roleCode}`;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Role Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.roleCode ? ' form-field-error' : ''}`}
                value={formData.roleCode}
                onChange={e => { setFormData(p => ({ ...p, roleCode: e.target.value.toUpperCase() })); setFieldErrors(prev => { const { roleCode, ...rest } = prev; return rest; }); }}
                placeholder="e.g., ADMIN"
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={50}
              />
              {fieldErrors.roleCode && <div className="form-error-message">{fieldErrors.roleCode}</div>}
            </div>
            <div className="form-group">
              <SyutSelect
                label="Status"
                isAsync
                loadOptions={loadStatusOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={formData.statusCode}
                onChange={(val) => setFormData(p => ({ ...p, statusCode: (val as string) || 'ACTIVE' }))}
                placeholder="Select status..."
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Role Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${fieldErrors.roleName ? ' form-field-error' : ''}`}
              value={formData.roleName}
              onChange={e => { setFormData(p => ({ ...p, roleName: e.target.value })); setFieldErrors(prev => { const { roleName, ...rest } = prev; return rest; }); }}
              placeholder="e.g., Administrator"
              maxLength={150}
            />
            {fieldErrors.roleName && <div className="form-error-message">{fieldErrors.roleName}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              placeholder="Role description"
              rows={3}
              maxLength={500}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Create Role'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={
          mode === 'edit'
            ? `Save changes to role "${formData.roleName}"?`
            : `Create role "${formData.roleName}"?`
        }
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={closeConfirmOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close? Your changes will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={() => { setCloseConfirmOpen(false); onClose(); }}
        onCancel={() => setCloseConfirmOpen(false)}
        variant="danger"
      />
    </>
  );
};

// ============================================================================
// ROLE MASTER SCREEN
// ============================================================================

interface RoleMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

type GridRecord = Omit<RoleDto, 'id'> & { id: string };

const RoleMasterScreen: React.FC<RoleMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized } = useModalMinimize();

  // Grid state
  const [queryParams, setQueryParams] = useState<RoleQueryParams>({ page: 1, pageSize: 25 });
  const [data, setData] = useState<RoleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // View & filter state
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<RoleDto | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // Load data — silent=true skips loading spinner (used for background refetch after save/delete)
  const loadData = useCallback(async (params: RoleQueryParams, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await roleService.getAll(params);
      setData(res.data);
      setTotalCount(res.pagination.totalItems);
    } catch {
      notificationService.toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(queryParams);
  }, [queryParams, loadData]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const refetch = useCallback(() => loadData(queryParams, true), [loadData, queryParams]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // CRUD
  const handleAdd = useCallback(() => {
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    createInstanceCounter.current += 1;
    setCreateInstanceId(String(createInstanceCounter.current));
    setSelectedRecord(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback((role: RoleDto) => {
    const modalId = `${tabId}-role-modal-${role.id}`;
    if (tabId && isMinimized(modalId)) {
      dismissMinimized(modalId);
      setSelectedRecord(role);
      setFormMode('edit');
      setFormModalOpen(true);
      return;
    }
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    setSelectedRecord(role);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [formModalOpen, tabId, isMinimized, dismissMinimized]);

  const handleCopy = useCallback((role: RoleDto) => {
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    copyInstanceCounter.current += 1;
    setCopyInstanceId(String(copyInstanceCounter.current));
    setSelectedRecord(role);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleDeleteRequest = useCallback((role: RoleDto) => {
    if (role.isSystem) {
      notificationService.toast.error('System roles cannot be deleted.');
      return;
    }
    setSelectedRecord(role);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedRecord) return;
    setIsDeleting(true);
    try {
      await roleService.delete(selectedRecord.id);
      notificationService.toast.success(`Role "${selectedRecord.roleName}" has been deleted successfully.`);
      setDeleteModalOpen(false);
      setSelectedRecord(null);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to delete role');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRecord, refetch]);

  // Restore handler for minimize
  const handleRestoreModal = useCallback((mode: 'create' | 'edit' | 'copy', instanceId?: string, roleCode?: string) => {
    if (instanceId) {
      if (mode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (roleCode) {
      const rec = data.find(r => r.roleCode === roleCode);
      if (rec) setSelectedRecord(rec);
    }
    setFormMode(mode);
    setFormModalOpen(true);
  }, [data]);

  // Grid columns
  const columns: SyutGridColumn<GridRecord>[] = [
    {
      id: 'roleCode',
      header: 'Code',
      accessorKey: 'roleCode',
      size: 120,
      cell: ({ row }) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.original.roleCode}</span>
      ),
    },
    { id: 'roleName', header: 'Role Name', accessorKey: 'roleName', size: 220 },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 280 },
    {
      id: 'isSystem',
      header: 'System',
      accessorKey: 'isSystem',
      size: 80,
      cell: ({ row }) => row.original.isSystem ? (
        <span className="badge badge-warning">System</span>
      ) : null,
    },
    {
      id: 'userCount',
      header: 'Users',
      accessorKey: 'userCount',
      size: 70,
      cell: ({ row }) => (
        <span style={{ textAlign: 'right', display: 'block' }}>{row.original.userCount ?? 0}</span>
      ),
    },
    {
      id: 'statusName',
      header: 'Status',
      accessorKey: 'statusName',
      size: 100,
      cell: ({ row }) => row.original.statusName ? (
        <span className="badge" style={{ background: row.original.statusColor || 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
          {row.original.statusName}
        </span>
      ) : null,
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 70,
      cell: ({ row }) => {
        const role = row.original as RoleDto;
        const items: ActionMenuItem[] = [
          { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={14} />, onClick: () => handleEdit(role) },
          { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={14} />, onClick: () => handleCopy(role) },
          {
            id: 'delete',
            label: 'Delete',
            icon: <IconSystem.trash size={14} />,
            onClick: () => handleDeleteRequest(role),
            variant: 'danger',
            disabled: role.isSystem,
          },
        ];
        return <ActionMenu items={items} />;
      },
    },
  ];

  const gridData = data as unknown as GridRecord[];

  // View mode options (matches SampleMasterScreen pattern)
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View (Not Applicable)', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  // Action buttons for ModuleHeader (icon-only — matches SampleMasterScreen pattern)
  const actionButtons: ActionButton[] = [
    { id: 'add',     icon: <IconSystem.add size={18} />,      label: 'Add Role', onClick: handleAdd, tooltip: 'Add New Role',  variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />,  label: 'Refresh',  onClick: refetch,   tooltip: 'Refresh',       variant: 'icon' },
    { id: 'export',  icon: <IconSystem.download size={18} />, label: 'Export',   onClick: () => {},  tooltip: 'Export',         variant: 'icon' },
  ];

  // Filter handlers
  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      statusFilter: statusFilter || undefined,
      isActive: activeFilter === 'true' ? true : activeFilter === 'false' ? false : undefined,
      page: 1,
    }));
  }, [statusFilter, activeFilter]);

  const handleFilterClear = useCallback(() => {
    setStatusFilter('');
    setActiveFilter('');
    setQueryParams(prev => ({
      ...prev,
      statusFilter: undefined,
      isActive: undefined,
      page: 1,
    }));
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Role Master"
        description="Define and manage roles for access control"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search roles...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as 'list' | 'grid') }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={gridData}
            columns={columns}
            isLoading={loading}
            manualPagination
            totalCount={totalCount}
            currentPage={(queryParams.page ?? 1) - 1}
            pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            emptyMessage="No roles found"
          />
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <span>Filters</span>
              <button className="btn-icon" onClick={() => setFilterOpen(false)} title="Close">
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <SyutSelect
                label="Status"
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
                value={activeFilter}
                onChange={val => setActiveFilter(val as string || '')}
                isClearable
              />
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary btn-sm" onClick={handleFilterClear}>Clear</button>
              <button className="btn btn-primary btn-sm" onClick={handleFilterApply}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <RoleFormModal
        open={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onClose={() => { setFormModalOpen(false); setSelectedRecord(null); }}
        onSave={handleSaveSuccess}
        tabId={tabId}
        moduleName={moduleName}
        instanceId={formMode === 'copy' ? copyInstanceId : createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Role"
        message={`Are you sure you want to delete role "${selectedRecord?.roleName}"? This action cannot be undone.`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteModalOpen(false); setSelectedRecord(null); }}
        variant="danger"
      />
    </div>
  );
};

export default RoleMasterScreen;
